import React from 'react';
import { Box, Typography, ButtonBase, CircularProgress } from '@mui/material';
import { useTranslation } from 'react-i18next';
import StatusTag, { TagState } from '@/components/shared/Tags.tsx';
import RadioItemGroup from '@/components/shared/RadioItemGroup.tsx';
import { useApiStore, useSettingsStore } from '@core/store';
import { OperationModeSchedule } from '@features/shopautomation/types.ts';
import { useModeTagState } from '@core/hooks/useModeTagState.ts';

interface ManageOperationModeProps {
    schedulesEnabled: boolean;
    onActiveOperationMode: (enabled: boolean) => void;
    isOperationModeScheduleActive: boolean;
    disabled: boolean;
    activeOperationModes: string[];
    onChangeOperationMode: (operationMode: string) => void;
    loadingScheduledInfo?: boolean;
    getShopOperationMode: (shopId: string) => string | undefined;
    shopId: string;
    currentScheduledState?: OperationModeSchedule;
    upcomingScheduledState?: OperationModeSchedule;
}

const StateHeaderRow: React.FC<{ label: string; time?: string }> = ({ label, time }) => (
    <>
        <Typography className="headerLabel">{label}</Typography>

        <Typography className="headerTime">{time}</Typography>
    </>
);

const StateValueRow: React.FC<{
    loading?: boolean;
    state?: OperationModeSchedule;
    tagState: TagState;
    isUpcomingSchedule?: boolean;
}> = ({ loading, state, tagState, isUpcomingSchedule }) => (
    <>
        <Box className="valueBadgeCell">
            {loading ? (
                <CircularProgress size={18} />
            ) : state ? (
                <StatusTag state={tagState} label={state.operationMode ?? '—'} />
            ) : (
                <Typography sx={{ fontSize: '18px' }}>-</Typography>
            )}
        </Box>
        {isUpcomingSchedule && (
            <Box sx={{ pr: 1 }}>
                <Typography className="valueTime">{state ? `${state.from ?? ''}` : '-'}</Typography>
                {!!state?.date && (
                    <Typography className="headerLabel">{state ? `${state.date ?? ''}` : '-'}</Typography>
                )}
            </Box>
        )}
    </>
);

const ManageOperationMode: React.FC<ManageOperationModeProps> = ({
    schedulesEnabled,
    isOperationModeScheduleActive,
    onActiveOperationMode,
    disabled,
    activeOperationModes,
    onChangeOperationMode,
    loadingScheduledInfo,
    getShopOperationMode,
    shopId,
    currentScheduledState,
    upcomingScheduledState
}) => {
    const { t } = useTranslation();
    const { isMobile } = useSettingsStore();
    const { operationModes } = useApiStore();
    const getModeTagState = useModeTagState();

    if (isMobile) {
        return (
            <Box className="operation-mode-container mobile">
                <Box className="radioPanel">
                    <RadioItemGroup
                        options={[
                            { label: t('operationModeSchedules.timed'), value: 'TIMED' },
                            { label: t('operationModeSchedules.manual'), value: 'MANUAL' }
                        ]}
                        value={isOperationModeScheduleActive ? 'TIMED' : 'MANUAL'}
                        onChange={(e) => onActiveOperationMode(e === 'TIMED')}
                        disabled={!schedulesEnabled || disabled}
                    />
                </Box>

                {isOperationModeScheduleActive ? (
                    <Box className="timedGroup">
                        {loadingScheduledInfo && !currentScheduledState && (
                            <CircularProgress size={18} sx={{ mt: 2 }} />
                        )}
                        {currentScheduledState && (
                            <Box className="stateBoxCurrent-mobile">
                                <StateHeaderRow label={t('operationModeSchedules.currentState')} />
                                <StateValueRow
                                    loading={loadingScheduledInfo}
                                    state={currentScheduledState}
                                    tagState={getModeTagState(currentScheduledState?.operationMode ?? '')}
                                />
                            </Box>
                        )}
                        {upcomingScheduledState && (
                            <Box className="stateBoxUpcoming-mobile">
                                <StateHeaderRow
                                    label={t('operationModeSchedules.upcomingState')}
                                    time={
                                        upcomingScheduledState?.date ? t('operationModeSchedules.starting') : undefined
                                    }
                                />
                                <StateValueRow
                                    loading={loadingScheduledInfo}
                                    state={upcomingScheduledState}
                                    tagState={getModeTagState(upcomingScheduledState?.operationMode ?? '')}
                                    isUpcomingSchedule={true}
                                />
                            </Box>
                        )}
                    </Box>
                ) : (
                    <Box className="manualGroup">
                        {(() => {
                            const currentOperationMode = getShopOperationMode(shopId);
                            const sortedModes = [...activeOperationModes].sort((a, b) => {
                                if (a === currentOperationMode) return -1;
                                if (b === currentOperationMode) return 1;
                                return 0;
                            });

                            return sortedModes.map((mode) => {
                                const isActive = mode === currentOperationMode;
                                const currentMode = operationModes.find((m) => m.name === mode);
                                return (
                                    <ButtonBase
                                        key={mode}
                                        onClick={() => onChangeOperationMode(mode)}
                                        disabled={disabled}
                                        className={`switchBox ${isActive ? 'mode-active' : ''}`}
                                    >
                                        <Box sx={{ p: 1 }}>
                                            {isActive ? (
                                                <i className="bi bi-pin-angle switchIcon" />
                                            ) : (
                                                <i className="bi-arrow-left-right switchIcon" />
                                            )}
                                        </Box>
                                        <Box className="switchLabelGroup">
                                            <Typography className="switchLabel">
                                                {isActive
                                                    ? t('operationModeSchedules.fixedState')
                                                    : t('operationModeSchedules.switchTo')}
                                            </Typography>
                                            <StatusTag state={getModeTagState(mode)} />
                                        </Box>
                                    </ButtonBase>
                                );
                            });
                        })()}
                    </Box>
                )}
            </Box>
        );
    }

    return (
        <Box className="operation-mode-container">
            <Box className="radioPanel">
                <RadioItemGroup
                    options={[
                        { label: t('operationModeSchedules.timed'), value: 'TIMED' },
                        { label: t('operationModeSchedules.manual'), value: 'MANUAL' }
                    ]}
                    value={isOperationModeScheduleActive ? 'TIMED' : 'MANUAL'}
                    onChange={(e) => onActiveOperationMode(e === 'TIMED')}
                    disabled={!schedulesEnabled || disabled}
                />
            </Box>

            {isOperationModeScheduleActive ? (
                <Box className="timedGroup">
                    {loadingScheduledInfo && !currentScheduledState && (
                        <CircularProgress size={18} sx={{ placeSelf: 'center' }} />
                    )}
                    {currentScheduledState && (
                        <Box className="stateBoxCurrent">
                            <StateHeaderRow label={t('operationModeSchedules.currentState')} />
                            <StateValueRow
                                loading={loadingScheduledInfo}
                                state={currentScheduledState}
                                tagState={getModeTagState(currentScheduledState?.operationMode ?? '')}
                            />
                        </Box>
                    )}
                    {upcomingScheduledState && (
                        <>
                            <Box className="connectorWrapper">
                                <svg width="30" height="40" viewBox="0 0 30 40">
                                    <polygon points="0,0 0,40 30,20" className="connectorFill" stroke="none" />
                                    <polyline points="14,9.33 30,20 14,30.67" className="connectorStroke" />
                                </svg>
                            </Box>

                            <Box className="stateBoxUpcoming">
                                <StateHeaderRow
                                    label={t('operationModeSchedules.upcomingState')}
                                    time={
                                        upcomingScheduledState?.date ? t('operationModeSchedules.starting') : undefined
                                    }
                                />
                                <StateValueRow
                                    loading={loadingScheduledInfo}
                                    state={upcomingScheduledState}
                                    tagState={getModeTagState(upcomingScheduledState?.operationMode ?? '')}
                                    isUpcomingSchedule={true}
                                />
                            </Box>
                        </>
                    )}
                </Box>
            ) : (
                <Box className="manualGroup">
                    {(() => {
                        const currentOperationMode = getShopOperationMode(shopId);
                        const sortedModes = [...activeOperationModes].sort((a, b) => {
                            if (a === currentOperationMode) return -1;
                            if (b === currentOperationMode) return 1;
                            return 0;
                        });

                        return sortedModes.map((mode) => {
                            const isActive = mode === currentOperationMode;
                            return (
                                <ButtonBase
                                    key={mode}
                                    onClick={() => onChangeOperationMode(mode)}
                                    disabled={disabled}
                                    className={`switchBox ${isActive ? 'mode-active' : ''}`}
                                >
                                    <Box sx={{ p: 1 }}>
                                        {isActive ? (
                                            <i className="bi bi-pin-angle switchIcon" />
                                        ) : (
                                            <i className="bi-arrow-left-right switchIcon" />
                                        )}
                                    </Box>
                                    <Box>
                                        <Typography className="switchLabel">
                                            {isActive
                                                ? t('operationModeSchedules.fixedState')
                                                : t('operationModeSchedules.switchTo')}
                                        </Typography>
                                        <Box sx={{ mt: 0.5 }}>
                                            <StatusTag state={getModeTagState(mode)} label={mode ?? '—'} />
                                        </Box>
                                    </Box>
                                </ButtonBase>
                            );
                        });
                    })()}
                </Box>
            )}
        </Box>
    );
};

export default ManageOperationMode;
